import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { GALLERY, GALLERY_CATEGORIES } from "@/data/site";

export default function Gallery() {
  const [category, setCategory] = useState("All");
  const [selected, setSelected] = useState(null);

  const filtered = category === "All" ? GALLERY : GALLERY.filter((g) => g.category === category);

  useEffect(() => {
    const onKey = (e) => {
      if (selected === null) return;
      if (e.key === "Escape") setSelected(null);
      if (e.key === "ArrowRight") setSelected((s) => (s + 1) % filtered.length);
      if (e.key === "ArrowLeft") setSelected((s) => (s - 1 + filtered.length) % filtered.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected, filtered.length]);

  return (
    <div data-testid="gallery-page">
      <Seo
        title="Project Gallery - Kitchens, Bathrooms, Exteriors | McMullen"
        description="Browse McMullen Construction Co. projects: custom kitchens, spa bathrooms, home exteriors and ground-up new construction across Florida."
      />
      <PageHero
        testId="gallery-hero"
        eyebrow="Portfolio"
        lines={["Work that speaks", "for itself."]}
        intro="Exteriors, kitchens, bathrooms and ground-up builds - every photograph is a McMullen project, finished and lived in."
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24" data-testid="gallery-grid-section">
        <Reveal>
          <div className="flex flex-wrap gap-2 mb-12" role="group" aria-label="Filter gallery by category">
            {GALLERY_CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                aria-pressed={category === c}
                data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-5 py-2.5 text-xs uppercase tracking-[0.2em] border transition-colors duration-300 ${
                  category === c
                    ? "border-[#D4AF37] bg-[#D4AF37] text-black font-semibold"
                    : "border-[#27272A] text-[#A1A1AA] hover:border-[rgba(212,175,55,0.5)] hover:text-[#F3E081]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((item, i) => (
            <Reveal key={item.title} delay={(i % 3) * 0.08}>
              <button
                type="button"
                onClick={() => setSelected(i)}
                data-testid={`gallery-item-${i}`}
                className="group relative block w-full h-72 overflow-hidden border border-[rgba(212,175,55,0.2)] hover:border-[rgba(243,224,129,0.55)] transition-colors duration-500 text-left"
                aria-label={`Open photo: ${item.title}`}
              >
                <img
                  src={item.src}
                  alt={item.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover img-spotlight transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#08080A]/95 via-transparent to-transparent opacity-90" aria-hidden="true" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{item.category}</p>
                  <p className="mt-1 font-display text-lg text-[#F4F2EE]">{item.title}</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {selected !== null && filtered[selected] && (
          <motion.div
            key="lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setSelected(null)}
            role="dialog"
            aria-modal="true"
            aria-label={filtered[selected].title}
            data-testid="gallery-lightbox"
          >
            <button
              type="button"
              onClick={() => setSelected(null)}
              aria-label="Close photo viewer"
              data-testid="lightbox-close"
              className="absolute top-5 right-5 p-2 text-[#A1A1AA] hover:text-[#F3E081] z-10"
            >
              <X className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setSelected((s) => (s - 1 + filtered.length) % filtered.length); }}
              aria-label="Previous photo"
              data-testid="lightbox-prev"
              className="absolute left-3 sm:left-6 p-3 text-[#D4AF37] hover:text-[#F3E081] z-10"
            >
              <ChevronLeft className="h-8 w-8" />
            </button>
            <motion.figure
              key={filtered[selected].title}
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-4xl w-full"
            >
              <img src={filtered[selected].src} alt={filtered[selected].title} className="w-full max-h-[75vh] object-contain gold-frame" />
              <figcaption className="mt-4 text-center">
                <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{filtered[selected].category}</p>
                <p className="mt-1 font-display text-xl text-[#F4F2EE]">{filtered[selected].title}</p>
                <p className="mt-1 text-sm text-[#A1A1AA]">{filtered[selected].desc}</p>
              </figcaption>
            </motion.figure>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setSelected((s) => (s + 1) % filtered.length); }}
              aria-label="Next photo"
              data-testid="lightbox-next"
              className="absolute right-3 sm:right-6 p-3 text-[#D4AF37] hover:text-[#F3E081] z-10"
            >
              <ChevronRight className="h-8 w-8" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
